import { useQueryClient, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  useGetUserSegmentsQuery,
  CreateUserSegmentMutation,
  CreateUserSegmentMutationVariables,
  GetUserSegmentsQuery,
  UserSegmentFragment,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = CreateUserSegmentMutation;
type MutationVariables = CreateUserSegmentMutationVariables;
type QueryData = GetUserSegmentsQuery;

const ID_STUB = '-1';

const useCreateSegment = () => {
  const queryClient = useQueryClient();
  const queryKey = useGetUserSegmentsQuery.getKey({});
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    // TODO: Insert into correct alphabetical position
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(queryKey);

      const newFollowMapping: UserSegmentFragment = {
        id: ID_STUB,
        ...mutationArgs.data,
      };

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.getUserSegments.unshift(newFollowMapping);
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: ({ createUserSegment }) => {
      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          const index = draftState.getUserSegments.findIndex(segment => segment.id === ID_STUB);
          draftState.getUserSegments[index] = createUserSegment;
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }
    },
  };

  return mutationOptions;
};

export default useCreateSegment;
